import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import type { ProjectResponse, ProjectMode } from "../../types/api";
import { projectAPI } from "../../lib/api";
import styles from "./ProjectCard.module.css";

interface ProjectCardProps {
  project: ProjectResponse;
  orgId: string;
  onDelete?: (projectId: string) => void;
  onRename?: (projectId: string, newTitle: string) => void;
}

const modeKeys: Record<ProjectMode, string> = {
  stream: "modes.stream",
  outline: "modes.outline",
  edit: "modes.draft",
};

function ModeIcon({ mode }: { mode: ProjectMode }) {
  switch (mode) {
    case "stream":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 2V14M4 5L8 2L12 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "outline":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 4H14M5 8H14M5 12H11M2 4H2.01M2 8H2.01M2 12H2.01"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "edit":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 2H4C3.44772 2 3 2.44772 3 3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V6L9 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 2V6H13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
}

function useFormatRelativeTime() {
  const { t } = useTranslation();
  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t("projects.timeJustNow");
    if (diffMins < 60) return t("projects.timeMinutesAgo", { count: diffMins });
    if (diffHours < 24) return t("projects.timeHoursAgo", { count: diffHours });
    if (diffDays < 7) return t("projects.timeDaysAgo", { count: diffDays });

    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
}

/**
 * Returns true when the content preview is redundant with the title,
 * e.g. the title was auto-generated from the first sentence.
 */
function isPreviewRedundant(title: string, preview: string): boolean {
  const t = title.trim().toLowerCase();
  const p = preview.trim().toLowerCase();
  if (t === p) return true;
  // Only treat as redundant when the shared prefix is long enough to be
  // meaningful (at least 20 chars or the full length of the shorter string
  // when it's reasonably sized). This avoids false positives for short/generic
  // titles like "AI" matching "AI is changing hiring…".
  const minLen = Math.min(t.length, p.length);
  if (minLen < 20) return false;
  return t.startsWith(p) || p.startsWith(t);
}

export function ProjectCard({ project, orgId, onDelete, onRename }: ProjectCardProps) {
  const { t } = useTranslation();
  const formatRelativeTime = useFormatRelativeTime();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(project.title);
  const [renameLoading, setRenameLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    };
    const handleScroll = () => {
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (renaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renaming]);

  const projectUrl = `/organizations/${orgId}/projects/${project.id}`;

  const handleRenameStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRenameValue(project.title);
    setRenaming(true);
    setMenuOpen(false);
  };

  const handleRenameSubmit = async () => {
    if (renameLoading) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === project.title) {
      setRenaming(false);
      return;
    }
    setRenameLoading(true);
    try {
      await projectAPI.update(orgId, project.id, { title: trimmed });
      onRename?.(project.id, trimmed);
      toast.success(t("projects.projectRenamed"));
    } catch {
      toast.error(t("projects.failedToRename"));
    } finally {
      setRenameLoading(false);
      setRenaming(false);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      setRenameValue(project.title);
      setRenaming(false);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete?.(project.id);
  };

  return (
    <article className={`${styles.card} ${menuOpen ? styles.cardMenuOpen : ""}`}>
      {/* Primary navigation link — covers the entire card via CSS so the
          card is natively navigable (right-click, middle-click, keyboard).
          Interactive children (menu, rename) sit above via z-index. */}
      <Link
        to={projectUrl}
        className={styles.cardLink}
        aria-label={project.title}
        tabIndex={renaming ? -1 : 0}
        onClick={(e) => {
          if (renaming) e.preventDefault();
        }}
      />

      <div className={styles.cardHeader}>
        {renaming ? (
          <input
            ref={renameInputRef}
            className={styles.renameInput}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={handleRenameSubmit}
            onClick={(e) => e.stopPropagation()}
            disabled={renameLoading}
            maxLength={200}
          />
        ) : (
          <h3 className={styles.title} title={project.title}>
            {project.title}
          </h3>
        )}
        <div className={styles.headerRight}>
          <span
            className={styles.modeIcon}
            data-mode={project.current_mode}
            title={t(modeKeys[project.current_mode])}
            aria-label={t(modeKeys[project.current_mode])}
          >
            <ModeIcon mode={project.current_mode} />
          </span>
          {onDelete && (
            <div className={styles.menuWrapper} ref={menuRef}>
              <button
                type="button"
                className={styles.menuTrigger}
                aria-label={t("projects.projectOptions")}
                onMouseDown={(e) => {
                  // Prevent the stretched <Link> overlay from capturing
                  // this interaction and requiring a double-click.
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setMenuOpen((prev) => !prev);
                }}
              >
                ⋮
              </button>
              {menuOpen && (
                <div className={styles.menu}>
                  <button type="button" className={styles.menuItem} onClick={handleRenameStart}>
                    {t("projects.renameProject")}
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuItem} ${styles.danger}`}
                    onClick={handleDelete}
                  >
                    {t("projects.deleteProject")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {(() => {
        const sanitizedPreview = project.content_preview
          ?.replace(/\\r\\n|\\n|\\r|\r\n|\n|\r/g, " ")
          .trim();
        return sanitizedPreview && !isPreviewRedundant(project.title, sanitizedPreview) ? (
          <p className={styles.contentPreview}>{sanitizedPreview}</p>
        ) : null;
      })()}

      <div className={styles.meta}>
        <span className={styles.metaItem}>
          {project.word_count > 0
            ? t("projects.wordCount", { count: project.word_count })
            : t("projects.noWords")}
        </span>
        <span className={styles.metaSeparator}>·</span>
        <span className={styles.metaItem}>
          {t("projects.updated")} {formatRelativeTime(project.updated_at)}
        </span>
      </div>
    </article>
  );
}
